
import React, { Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Star, Users, Award } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import TherapistCard from '@/components/therapists/TherapistCard';
import { T } from '@/components/T';

const FeaturedTherapistsSection = () => {
  // Mock data for featured therapists
  const featuredTherapists = [
    {
      id: '1',
      name: 'Dr. Sarah Johnson',
      title: 'Clinical Psychologist',
      specialties: ['Anxiety', 'Depression', 'Trauma Therapy'],
      rating: 4.9,
      reviews: 127,
      location: 'New York, US',
      languages: ['English', 'Spanish'],
      price: 120,
      currency: '$',
      image: '/placeholder.svg',
      verified: true,
      onlineAvailable: true,
      nextAvailable: 'Today 3:00 PM'
    },
    {
      id: '2',
      name: 'Dr. Maria Rodriguez',
      title: 'Licensed Therapist',
      specialties: ['Relationship Counseling', 'Family Therapy', 'CBT'],
      rating: 4.8,
      reviews: 89,
      location: 'Madrid, Spain',
      languages: ['Spanish', 'English', 'French'],
      price: 85,
      currency: '€',
      image: '/placeholder.svg',
      verified: true,
      onlineAvailable: true,
      nextAvailable: 'Tomorrow 10:00 AM'
    },
    {
      id: '3',
      name: 'Dr. James Chen',
      title: 'Psychiatric Specialist',
      specialties: ['ADHD', 'Autism Spectrum', 'Child Psychology'],
      rating: 4.9,
      reviews: 203,
      location: 'Singapore',
      languages: ['English', 'Mandarin', 'Cantonese'],
      price: 95,
      currency: '$',
      image: '/placeholder.svg',
      verified: true,
      onlineAvailable: true,
      nextAvailable: 'Today 6:00 PM'
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            <T>Featured Therapists</T>
          </h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-6">
            <T>Connect with our top-rated mental health professionals who are ready to help you on your journey to wellness.</T>
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-full">
              <Star className="h-4 w-4 text-blue-600" />
              <span className="text-blue-800"><T>Top Rated</T></span>
            </div>
            <div className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-full">
              <Award className="h-4 w-4 text-green-600" />
              <span className="text-green-800"><T>Verified Professionals</T></span>
            </div>
            <div className="flex items-center gap-2 bg-purple-50 px-3 py-1.5 rounded-full">
              <Users className="h-4 w-4 text-purple-600" />
              <span className="text-purple-800"><T>Available Now</T></span>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Suspense fallback={
            <div className="space-y-3">
              <Skeleton className="h-48 w-full rounded-lg" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          }>
            {featuredTherapists.map((therapist) => (
              <TherapistCard 
                key={therapist.id} 
                therapist={therapist}
                showBookingButton={true}
              />
            ))}
          </Suspense>
        </div>
        
        <div className="text-center">
          <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
            <Link to="/directory" className="flex items-center gap-2">
              <T>Browse All Therapists</T>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedTherapistsSection;
